import React, { useEffect } from 'react';
import { Modal, Form, Input, InputNumber, Select, DatePicker, Switch, Row, Col } from 'antd';
import moment from 'moment';

const { Option } = Select;
const { TextArea } = Input;

const ViewPricingPlanModal = ({ showModal, hideModal, pricingPlan }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (pricingPlan) {
      form.setFieldsValue({
        ...pricingPlan,
        effectiveFrom: pricingPlan.effectiveFrom ? moment(pricingPlan.effectiveFrom) : null,
        effectiveTo: pricingPlan.effectiveTo ? moment(pricingPlan.effectiveTo) : null,
        // Convert dates in pricingRules
        pricingRules: pricingPlan.pricingRules && Array.isArray(pricingPlan.pricingRules)
          ? pricingPlan.pricingRules.map(rule => ({
              ...rule,
              effectiveFrom: rule.effectiveFrom ? moment(rule.effectiveFrom) : null,
              effectiveTo: rule.effectiveTo ? moment(rule.effectiveTo) : null,
            }))
          : undefined,
      });
    }
  }, [pricingPlan, form]);

  return (
    <Modal
      title={`Pricing Plan Details: ${pricingPlan?.pricingName}`}
      open={showModal}
      onCancel={hideModal}
      footer={null} // No footer buttons for a view-only modal
      width={800}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ isActive: true, currency: 'NGN', pricingModel: 'FIXED', priority: 1 }}
      >
        <Form.Item name="pricingName" label="Pricing Plan Name">
          <Input readOnly />
        </Form.Item>
        <Form.Item name="pricingDescription" label="Description">
          <TextArea rows={2} readOnly />
        </Form.Item>
        <Form.Item name="pricingModel" label="Pricing Model">
          <Select disabled>
            <Option value="FIXED">FIXED</Option>
            <Option value="TIERED">TIERED</Option>
            <Option value="PERCENTAGE">PERCENTAGE</Option>
            <Option value="DYNAMIC">DYNAMIC</Option>
            <Option value="SUBSCRIPTION">SUBSCRIPTION</Option>
            <Option value="USAGE_BASED">USAGE_BASED</Option>
            <Option value="BUNDLE">BUNDLE</Option>
          </Select>
        </Form.Item>

        <Form.Item name="basePrice" label="Base Price">
          <InputNumber min={0} style={{ width: '100%' }} readOnly />
        </Form.Item>
        <Form.Item name="currency" label="Currency">
          <Select disabled>
            <Option value="NGN">NGN</Option>
            <Option value="USD">USD</Option>
          </Select>
        </Form.Item>

        <Form.Item name="effectiveFrom" label="Effective From">
          <DatePicker showTime style={{ width: '100%' }} disabled />
        </Form.Item>
        <Form.Item name="effectiveTo" label="Effective To">
          <DatePicker showTime style={{ width: '100%' }} disabled />
        </Form.Item>
        <Form.Item name="isActive" label="Is Active" valuePropName="checked">
          <Switch disabled />
        </Form.Item>
        <Form.Item name="priority" label="Priority">
          <InputNumber min={1} style={{ width: '100%' }} readOnly />
        </Form.Item>

        <Form.Item
          name="metadata"
          label="Metadata (JSON string)"
        >
          <TextArea rows={2} readOnly />
        </Form.Item>

        {(pricingPlan?.pricingModel === 'TIERED' || pricingPlan?.pricingModel === 'DYNAMIC') && (
          <>
            <h3>Pricing Tiers</h3>
            <Form.List name="pricingTiers">
              {(fields) => ( // Removed add, remove as it's read-only
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{ border: '1px solid #d9d9d9', padding: '16px', marginBottom: '16px', borderRadius: '2px' }}>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item {...restField} name={[name, 'tierName']} label="Tier Name">
                            <Input placeholder="Tier Name" readOnly />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item {...restField} name={[name, 'tierDescription']} label="Tier Description">
                            <Input placeholder="Tier Description" readOnly />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item {...restField} name={[name, 'minQuantity']} label="Min Quantity">
                            <InputNumber placeholder="Min Quantity" min={0} style={{ width: '100%' }} readOnly />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item {...restField} name={[name, 'maxQuantity']} label="Max Quantity">
                            <InputNumber placeholder="Max Quantity" min={0} style={{ width: '100%' }} readOnly />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item {...restField} name={[name, 'minAmount']} label="Min Amount">
                            <InputNumber placeholder="Min Amount" min={0} style={{ width: '100%' }} readOnly />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item {...restField} name={[name, 'maxAmount']} label="Max Amount">
                            <InputNumber placeholder="Max Amount" min={0} style={{ width: '100%' }} readOnly />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item {...restField} name={[name, 'unitPrice']} label="Unit Price">
                            <InputNumber placeholder="Unit Price" min={0} style={{ width: '100%' }} readOnly />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item {...restField} name={[name, 'fixedFee']} label="Fixed Fee">
                            <InputNumber placeholder="Fixed Fee" min={0} style={{ width: '100%' }} readOnly />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item {...restField} name={[name, 'percentageRate']} label="Percentage Rate">
                            <InputNumber placeholder="Percentage Rate" min={0} max={1} step={0.01} style={{ width: '100%' }} readOnly />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item {...restField} name={[name, 'discountPercentage']} label="Discount %">
                            <InputNumber placeholder="Discount %" min={0} max={100} style={{ width: '100%' }} readOnly />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item {...restField} name={[name, 'priority']} label="Priority">
                            <InputNumber placeholder="Priority" min={1} style={{ width: '100%' }} readOnly />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item {...restField} name={[name, 'isActive']} label="Is Active" valuePropName="checked">
                            <Switch disabled />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </>
        )}

        {(pricingPlan?.pricingModel === 'DYNAMIC') && (
          <>
            <h3>Pricing Rules</h3>
            <Form.List name="pricingRules">
              {(fields) => ( // Removed add, remove as it's read-only
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{ border: '1px solid #d9d9d9', padding: '16px', marginBottom: '16px', borderRadius: '2px' }}>
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item {...restField} name={[name, 'ruleName']} label="Rule Name">
                          <Input placeholder="Rule Name" readOnly />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item {...restField} name={[name, 'ruleDescription']} label="Rule Description">
                          <Input placeholder="Rule Description" readOnly />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item {...restField} name={[name, 'ruleType']} label="Rule Type">
                          <Select disabled style={{ width: '100%' }}>
                            <Option value="AMOUNT_BASED">AMOUNT_BASED</Option>
                            <Option value="VOLUME_BASED">VOLUME_BASED</Option>
                            <Option value="TIME_BASED">TIME_BASED</Option>
                            <Option value="USER_BASED">USER_BASED</Option>
                            <Option value="MERCHANT_BASED">MERCHANT_BASED</Option>
                            <Option value="LOCATION_BASED">LOCATION_BASED</Option>
                            <Option value="CUSTOM">CUSTOM</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item {...restField} name={[name, 'conditionField']} label="Condition Field">
                          <Input placeholder="Condition Field" readOnly />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item {...restField} name={[name, 'conditionOperator']} label="Condition Operator">
                          <Select disabled style={{ width: '100%' }}>
                            <Option value="EQUALS">EQUALS</Option>
                            <Option value="NOT_EQUALS">NOT_EQUALS</Option>
                            <Option value="GREATER_THAN">GREATER_THAN</Option>
                            <Option value="GREATER_THAN_EQUALS">GREATER_THAN_EQUALS</Option>
                            <Option value="LESS_THAN">LESS_THAN</Option>
                            <Option value="LESS_THAN_EQUALS">LESS_THAN_EQUALS</Option>
                            <Option value="CONTAINS">CONTAINS</Option>
                            <Option value="NOT_CONTAINS">NOT_CONTAINS</Option>
                            <Option value="IN">IN</Option>
                            <Option value="NOT_IN">NOT_IN</Option>
                            <Option value="BETWEEN">BETWEEN</Option>
                            <Option value="NOT_BETWEEN">NOT_BETWEEN</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item {...restField} name={[name, 'conditionValue']} label="Condition Value">
                          <InputNumber placeholder="Condition Value" style={{ width: '100%' }} readOnly />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item {...restField} name={[name, 'actionType']} label="Action Type">
                          <Select disabled style={{ width: '100%' }}>
                            <Option value="DISCOUNT_PERCENTAGE">DISCOUNT_PERCENTAGE</Option>
                            <Option value="FIXED_DISCOUNT">FIXED_DISCOUNT</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item {...restField} name={[name, 'actionValue']} label="Action Value">
                          <InputNumber placeholder="Action Value" style={{ width: '100%' }} readOnly />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                       <Col xs={24} sm={12} md={4}>
                        <Form.Item {...restField} name={[name, 'effectiveFrom']} label="Rule Effective From">
                          <DatePicker showTime placeholder="Rule Effective From" style={{ width: '100%' }} disabled />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item {...restField} name={[name, 'effectiveTo']} label="Rule Effective To">
                          <DatePicker showTime placeholder="Rule Effective To" style={{ width: '100%' }} disabled />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item {...restField} name={[name, 'priority']} label="Priority">
                          <InputNumber placeholder="Priority" min={1} style={{ width: '100%' }} readOnly />
                        </Form.Item>
                      </Col>
                    </Row>
                     <Row gutter={16}>
                        <Col xs={24} sm={12} md={6}>
                          <Form.Item {...restField} name={[name, 'isActive']} label="Is Active" valuePropName="checked">
                            <Switch disabled />
                          </Form.Item>
                        </Col>
                     </Row>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default ViewPricingPlanModal;
